import React, { useCallback, useMemo, useRef } from 'react';
import { DataDiagramView } from './DataDiagramView';
import { FlyOutMenu, DesignerSurface } from 'domains/designers/common';
import { TabPanel } from 'components/tabs/TabPanel';
import { NameInputLayer } from './NameInputLayer';
import { DataListView } from './DataListView';
import { DataDesignerReducerAction, DataDesignerReducerState } from './DataDesignerReducer';
import {
  getDataFields,
  updateDiagram,
  deleteDiagrams,
  addDiagram,
  updateDiagramView,
  moveDiagramUp,
  moveDiagramDown,
  setConstantMappingValue,
  getEditDiagramMappingContextId,
  startEditingSelectInputMapping,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DataDesignerService';
import { IDesignerAction } from 'domains/designers/common/DesignerActions/DesignerAction';
import { getDiagramById, getDiagramsByIds } from './DataDesignerUtils';
import {
  alignLeft,
  alignRight,
  alignTop,
  alignBottom,
  alignCenterVertically,
  alignCenterHorizontally,
} from 'utils/AlignmentUtils';
import { distribute } from 'utils/GeometryUtils';
import {
  DataDesignerSurfaceType,
  DiagramDisplayData,
  DiagramView,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { isNotEmpty } from 'utils/ArrayUtils';
import {
  DiagramUpdateData,
  newDiagramUpdateData,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DisplayData/DiagramUpdateData';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { newAddDiagramDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DisplayData/AddDiagramDisplayData';

interface IDataDesignerSurfaceProps {
  /**
   * Designer session id
   */
  sessionId: string;
  /**
   * Surface state
   */
  state: DataDesignerReducerState;

  surfaceType: DataDesignerSurfaceType;
  /**
   * Changes local surface state.
   */
  dispatch: React.Dispatch<DataDesignerReducerAction>;
  /**
   * Callback to perform side effect when selection has been changed.
   */
  onSelectionChanged: (surface: DataDesignerSurfaceType, diagramIds: string[]) => void;
  /**
   *Callback to perform side effect when deleting diagrams.
   */
  onDeleteDiagrams: (surface: DataDesignerSurfaceType, diagramIds: string[]) => void;
  /**
   * should it have a column to edit default input mapping or not
   */
  showInputColumn: boolean;
}

/**
 * Surface contains two view: "Diagram View" and "List View".
 * It is reusable for inputs and outputs.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataDesignerSurface({
  children,
  sessionId,
  state,
  surfaceType,
  dispatch,
  onSelectionChanged,
  onDeleteDiagrams,
  showInputColumn,
}: React.PropsWithChildren<IDataDesignerSurfaceProps>) {
  const diagramViewRef = useRef<HTMLDivElement>(null);

  const selectDiagrams = useCallback(
    (diagramIds: string[]) => {
      dispatch({ type: 'select-diagrams', diagramIds: diagramIds });
      onSelectionChanged(surfaceType, diagramIds);
    },
    [dispatch, onSelectionChanged, surfaceType]
  );

  const handleDeleteDiagrams = useCallback(
    (diagramIds: string[]) => {
      deleteDiagrams({
        sessionId: sessionId,
        inputDataIds: diagramIds,
        surfaceType,
      });

      dispatch({ type: 'delete-diagrams', diagramIds: diagramIds });
      onDeleteDiagrams(surfaceType, diagramIds);
    },
    [dispatch, onDeleteDiagrams, sessionId, surfaceType]
  );

  const updateInput = useCallback(
    async (inputDataId: string, update: DiagramUpdateData) => {
      const input = await updateDiagram({
        sessionId: sessionId,
        inputDataId,
        diagramUpdate: update,
        surfaceType,
      });

      dispatch({
        type: 'update-diagram',
        diagram: input,
      });

      return input;
    },
    [dispatch, sessionId, surfaceType]
  );

  const loadFields = useCallback(
    (inputDataId: string, path: string) => {
      return getDataFields({
        inputDataId,
        path,
        sessionId: sessionId,
        surfaceType,
      });
    },
    [sessionId, surfaceType]
  );

  const handleAddedInputNameChanged = useCallback(
    async (newName: string, isList: boolean) => {
      if (state.layerForDiagramId != null) {
        const input = getDiagramById(state.diagrams, state.layerForDiagramId);

        if (input) {
          updateInput(
            state.layerForDiagramId,
            newDiagramUpdateData({
              Name: newName,
              IsList: isList,
              Type: input.DataDescription.Type,
            })
          );
          dispatch({ type: 'hide-name-layer' });
        }
        // Focus diagram view after adding setting name for diagram to be able to
        // interact with diagram view via keyboard (move/delete diagrams)
        diagramViewRef.current?.focus();
      }
    },
    [state.layerForDiagramId, state.diagrams, updateInput, dispatch]
  );

  const handleInputNameChanged = useCallback(
    (inputId: string, newName: string) => {
      const input = getDiagramById(state.diagrams, inputId);

      if (input) {
        return updateInput(
          inputId,
          newDiagramUpdateData({
            Name: newName,
            IsList: input.DataDescription.IsList,
            Type: input.DataDescription.Type,
          })
        ).then(input => input.DataDescription.Name);
      }

      return Promise.resolve('');
    },
    [state.diagrams, updateInput]
  );

  const handleSelectionChanged = useCallback(
    (diagramIds: string[]) => {
      selectDiagrams(diagramIds);
    },
    [selectDiagrams]
  );

  const handleInputIsListChanged = useCallback(
    (inputId: string, newValue: boolean) => {
      const input = getDiagramById(state.diagrams, inputId);

      if (input) {
        updateInput(
          inputId,
          newDiagramUpdateData({
            Name: input.DataDescription.Name,
            IsList: newValue,
            Type: input.DataDescription.Type,
          })
        );
      }
    },
    [state.diagrams, updateInput]
  );

  const handleInputTypeChanged = useCallback(
    (inputId: string, newType: DecisionsType | undefined) => {
      const input = getDiagramById(state.diagrams, inputId);

      if (input) {
        updateInput(
          inputId,
          newDiagramUpdateData({
            Name: input.DataDescription.Name,
            IsList: input.DataDescription.IsList,
            Type: newType,
          })
        );
      }
    },
    [state.diagrams, updateInput]
  );

  const handleDiagramViewUpdated = useCallback(
    (inputId: string, view: DiagramView) => {
      const input = getDiagramById(state.diagrams, inputId);

      if (input) {
        input.View = view;

        updateDiagramView({
          inputDataId: inputId,
          sessionId: sessionId,
          view: view,
          surfaceType,
        });

        dispatch({ type: 'update-diagram', diagram: input });
      }
    },
    [dispatch, sessionId, state.diagrams, surfaceType]
  );

  const handleAddRow = useCallback(async () => {
    //to prevent adding diagram when "Diagram View" is opened
    if (state.currentView === 'List View') {
      const input = await addDiagram({
        sessionId,
        surfaceType,
        diagram: newAddDiagramDisplayData({
          IsList: false,
          FullTypeName: 'System.String',
        }),
        ignoreChanges: true,
      });

      dispatch({
        type: 'add-diagram',
        input,
      });
    }
  }, [dispatch, sessionId, state.currentView, surfaceType]);

  const changeZIndex = useCallback(
    (zIndexGetter: (allDiagrams: DiagramDisplayData[], currentDiagram: DiagramDisplayData) => number) => {
      if (isNotEmpty(state.selectedDiagramIds)) {
        const requests: Promise<any>[] = state.selectedDiagramIds.map(inputId => {
          const allDiagrams = state.diagrams;
          const diagram = state.diagrams.find(item => item.Id === inputId);
          if (diagram) {
            const newView = {
              ...diagram.View,
              ZIndex: zIndexGetter(allDiagrams, diagram),
            };

            return updateDiagramView({
              sessionId: sessionId,
              inputDataId: inputId,
              view: newView,
              surfaceType,
            });
          } else {
            return Promise.resolve();
          }
        });

        Promise.all(requests);
      }
    },
    [sessionId, state.diagrams, state.selectedDiagramIds, surfaceType]
  );

  const handleGetEditMappingContextId = useCallback(
    async (inputId: string) => {
      const editingContextId = await getEditDiagramMappingContextId({
        sessionId: sessionId,
        inputDataId: inputId,
      });
      return editingContextId;
    },
    [sessionId]
  );

  const handleConstantValueChanged = useCallback(
    async (inputId: string, newValue: string) => {
      setConstantMappingValue({
        sessionId: sessionId,
        inputDataId: inputId,
        value: newValue,
      });
    },
    [sessionId]
  );

  const bringToFront = useCallback(() => {
    changeZIndex(all => Math.max(...all.map(item => item.View.ZIndex)) + 1);
  }, [changeZIndex]);

  const bringForward = useCallback(() => {
    changeZIndex((_, current) => current.View.ZIndex + 1);
  }, [changeZIndex]);

  const sendBackward = useCallback(() => {
    changeZIndex((_, current) => current.View.ZIndex - 1);
  }, [changeZIndex]);

  const sendToBack = useCallback(() => {
    changeZIndex((all, _) => Math.min(...all.map(item => item.View.ZIndex)) - 1);
  }, [changeZIndex]);

  const distributeSelectionVertically = useCallback(() => {
    if (state.selectedDiagramIds.length <= 1) return;

    const selectedDiagrams = getDiagramsByIds(state.diagrams, state.selectedDiagramIds);
    const distributedItems = distribute(
      selectedDiagrams,
      item => item.View.Top,
      item => item.View.Height
    );

    const requests = distributedItems.map(d => {
      const request = updateDiagramView({
        sessionId: sessionId,
        inputDataId: d.item.Id,
        view: {
          ...d.item.View,
          Top: d.newPosition,
        },
        surfaceType,
      });

      return request;
    });

    Promise.all(requests);
  }, [sessionId, state.diagrams, state.selectedDiagramIds, surfaceType]);

  const distributeSelectionHorizontally = useCallback(() => {
    if (state.selectedDiagramIds.length <= 1) return;

    const selectedDiagrams = getDiagramsByIds(state.diagrams, state.selectedDiagramIds);
    const distributedItems = distribute(
      selectedDiagrams,
      item => item.View.Left,
      item => item.View.Width
    );

    const requests = distributedItems.map(d => {
      const request = updateDiagramView({
        sessionId: sessionId,
        inputDataId: d.item.Id,
        view: {
          ...d.item.View,
          Left: d.newPosition,
        },
        surfaceType,
      });

      return request;
    });

    Promise.all(requests);
  }, [sessionId, state.diagrams, state.selectedDiagramIds, surfaceType]);

  const handleAlign: (alignFn: typeof alignLeft) => void = useCallback(
    alignFn => {
      if (state.selectedDiagramIds.length < 2) return;

      const selectedDiagrams = getDiagramsByIds(state.diagrams, state.selectedDiagramIds);

      const items = alignFn(selectedDiagrams, item => ({
        left: item.View.Left,
        top: item.View.Top,
        height: item.View.Height,
        width: item.View.Width,
      }));

      const requests = items.map(d => {
        const request = updateDiagramView({
          sessionId: sessionId,
          inputDataId: d.item.Id,
          view: {
            ...d.item.View,
            Left: d.left,
            Top: d.top,
          },
          surfaceType,
        });

        return request;
      });

      Promise.all(requests);
    },
    [sessionId, state.diagrams, state.selectedDiagramIds, surfaceType]
  );

  const flyOutActions = useMemo(() => {
    const actions: IDesignerAction[] = [];

    if (state.selectedDiagramIds == null) {
      return actions;
    }

    if (state.selectedDiagramIds.length > 0) {
      actions.push({
        name: 'Order',
        subActions: [
          { name: 'Bring to Front', action: bringToFront },
          { name: 'Bring Forward', action: bringForward },
          { name: 'Send Backward', action: sendBackward },
          { name: 'Send to Back', action: sendToBack },
        ],
      });
    }

    if (state.selectedDiagramIds.length > 1) {
      actions.push({
        name: 'Align',
        subActions: [
          { name: 'Left', action: () => handleAlign(alignLeft) },
          { name: 'Top', action: () => handleAlign(alignTop) },
          { name: 'Right', action: () => handleAlign(alignRight) },
          { name: 'Bottom', action: () => handleAlign(alignBottom) },
          { name: 'Center Vertically', action: () => handleAlign(alignCenterVertically) },
          { name: 'Center Horizontally', action: () => handleAlign(alignCenterHorizontally) },
        ],
      });

      actions.push({
        name: 'Distribute',
        subActions: [
          { name: 'Vertically', action: distributeSelectionVertically },
          { name: 'Horizontally', action: distributeSelectionHorizontally },
        ],
      });
    }

    if (state.selectedDiagramIds.length > 0) {
      actions.push({
        name: 'Delete',
        action: () => {
          handleDeleteDiagrams(state.selectedDiagramIds);
        },
      });
    }

    return actions;
  }, [
    bringForward,
    bringToFront,
    distributeSelectionHorizontally,
    distributeSelectionVertically,
    handleAlign,
    handleDeleteDiagrams,
    sendBackward,
    sendToBack,
    state.selectedDiagramIds,
  ]);

  const handleSelectRow = useCallback(
    (inputId?: string) => {
      if (inputId != null) {
        selectDiagrams([inputId]);
      } else {
        selectDiagrams([]);
      }
    },
    [selectDiagrams]
  );

  const handleDeleteRow = useCallback(
    (inputId: string) => {
      handleDeleteDiagrams([inputId]);
    },
    [handleDeleteDiagrams]
  );

  const handleRowMoveUp = useCallback(
    async (inputId: string) => {
      const diagrams = await moveDiagramUp({
        sessionId: sessionId,
        inputId,
        surfaceType,
      });
      dispatch({ type: 'set-diagrams', diagrams: diagrams });
    },
    [dispatch, sessionId, surfaceType]
  );

  const handleRowMoveDown = useCallback(
    async (inputId: string) => {
      const diagrams = await moveDiagramDown({
        sessionId: sessionId,
        inputId,
        surfaceType,
      });
      dispatch({ type: 'set-diagrams', diagrams: diagrams });
    },
    [dispatch, sessionId, surfaceType]
  );

  const startEditSelectMapping = useCallback(
    (id: string) => {
      return startEditingSelectInputMapping({
        sessionId,
        inputDataId: id,
      });
    },
    [sessionId]
  );

  const handleDiagramExpanded = useCallback(
    (diagramId: string) => {
      dispatch({ type: 'expand-diagram', diagramId });
    },
    [dispatch]
  );

  const handleDiagramCollapsed = useCallback(
    (diagramId: string) => {
      dispatch({ type: 'collapse-diagram', diagramId });
    },
    [dispatch]
  );

  const singleSelectedInput = (state.selectedDiagramIds ?? []).length !== 1 ? undefined : state.selectedDiagramIds?.[0];

  return (
    <DesignerSurface className="dpc-data-designer__surface">
      <TabPanel className="full-size" tabId="Diagram View" currentTabId={state.currentView}>
        {state.layerForDiagramId && (
          <NameInputLayer className="dpc-data-designer__input-name-layer" onInput={handleAddedInputNameChanged} />
        )}
        {flyOutActions.length > 0 && !state.layerForDiagramId && (
          <div className={'dpc-data-designer__flyout-menu-wrapper'}>
            <FlyOutMenu actions={flyOutActions} />
          </div>
        )}
        <DataDiagramView
          ref={diagramViewRef}
          selectedDiagramIds={state.selectedDiagramIds}
          expandedDiagramIds={state.expandedDiagramIds}
          diagrams={state.diagrams ?? []}
          getDataFields={loadFields}
          onNameChanged={handleInputNameChanged}
          onSelectionChanged={handleSelectionChanged}
          onDiagramViewUpdated={handleDiagramViewUpdated}
          onDeleteDiagrams={handleDeleteDiagrams}
          onDiagramExpanded={handleDiagramExpanded}
          onDiagramCollapsed={handleDiagramCollapsed}
        />
      </TabPanel>
      <TabPanel className="dpc-data-designer__list-view full-size" tabId="List View" currentTabId={state.currentView}>
        <DataListView
          diagrams={state.diagrams}
          showInputColumn={showInputColumn}
          selectedInputId={singleSelectedInput}
          onNameChanged={handleInputNameChanged}
          onIsListChanged={handleInputIsListChanged}
          onTypeChanged={handleInputTypeChanged}
          onRowDeleted={handleDeleteRow}
          onSelectionChanged={handleSelectRow}
          onRowMoveUp={handleRowMoveUp}
          onRowMoveDown={handleRowMoveDown}
          onAddRow={handleAddRow}
          onConstantValueChanged={handleConstantValueChanged}
          onGetEditMappingContextId={handleGetEditMappingContextId}
          startEditSelectMapping={startEditSelectMapping}
        />
      </TabPanel>
      {children}
    </DesignerSurface>
  );
}
